import { ROUTE_PATHS } from '../../../config/RoutePaths';
import {
  EMAIL_CENTER_TABS,
  EmailCenterTab,
  LEGACY_DESIGN_SECTION,
  LEGACY_SETTINGS_SECTIONS,
  LEGACY_TABS,
} from './constants';
import {
  DEFAULT_THEME_SECTION,
  parseThemeSection,
  ThemeSection,
} from './tabs/Theme/themeSections';

/**
 * Pure URL <-> state mapping for the Touchpoints pages that came out of the
 * Email Center. Tab/section/journey — and the template editor's open state —
 * live in query params so every view is deep-linkable:
 *   /touchpoints/email-center?tab=mailers
 *   /touchpoints/email-center?tab=layout&fragment=partials/footer
 *   /touchpoints/email-center?tab=journeys&journey=<hashId>
 *   /touchpoints/design?section=typography
 *   /touchpoints/translations?journey=<hashId>
 *   /touchpoints/email-center?template=<uuid>            (edit / read-only view)
 *   /touchpoints/email-center?template=<uuid>&view=preview
 *   /touchpoints/email-center?template=new&type=welcome  (create, preset trigger)
 * Defaults are omitted from the URL to keep links minimal.
 *
 * One parser serves all of them: a page reads only the params it renders, and
 * sharing the shape is what lets the journey picker and the template drawer work
 * the same way wherever they appear.
 */

/** `?template=new` — the create flow rather than an existing template. */
export const NEW_TEMPLATE_ID = 'new';
/**
 * `?type=marketing` — create preset for the trigger-less kind. The API models
 * that kind as `email_type: ''`, which a query param cannot carry legibly.
 */
export const MARKETING_TEMPLATE_TYPE = 'marketing';

export interface EmailCenterUrlState {
  tab: EmailCenterTab;
  journeyId: string | null;
  /**
   * Which part of the Design page is open. Only the Design page reads it; it is
   * also the param the retired Settings tab used, which is why the legacy
   * migration below reads the same name.
   */
  themeSection: ThemeSection;
  /**
   * Transient deep-link: a building-block key the Layout & Components tab should
   * open on arrival (e.g. from Cmd/Ctrl-clicking an `{% include %}` in a
   * template). Consumed and cleared once acted on.
   */
  fragmentKey: string | null;
  /**
   * The template the editor drawer is open for: a uuid, or NEW_TEMPLATE_ID
   * for the create flow. Null = drawer closed. `useTemplateDrawerRoute` is
   * what turns this into the actual drawer.
   */
  templateId: string | null;
  /** Editor view to land on when opening (e.g. 'preview'); null = its default. */
  templateView: string | null;
  /** Create preset: a trigger type key or MARKETING_TEMPLATE_TYPE; `new` only. */
  templateType: string | null;
}

export const DEFAULT_URL_STATE: EmailCenterUrlState = {
  tab: EMAIL_CENTER_TABS.TEMPLATES,
  themeSection: DEFAULT_THEME_SECTION,
  journeyId: null,
  fragmentKey: null,
  templateId: null,
  templateView: null,
  templateType: null,
};

const TAB_VALUES = Object.values(EMAIL_CENTER_TABS) as string[];

/**
 * `?tab=…&section=…` → the tab that section is served by now.
 *
 * Mailers and Translations used to be segmented sections inside a Settings tab.
 * Translations is a page of its own now and is handled by `legacyTabRedirect`;
 * what is left of that pair here is the Mailers half, which would otherwise land
 * on Templates because `settings` is no longer a tab.
 *
 * The theme's Email design section made the opposite move — out of the editor
 * and into the Layout & Components tab — so `?tab=theme&section=design` stays on
 * this page instead of being forwarded to the Design page with the rest of the
 * theme. A fragment deep-link means the same thing without naming a section: the
 * block editor is what it addresses, and that only exists in this tab.
 */
function migrateLegacyTab(
  rawTab: string,
  rawSection: string,
  rawFragment: string,
): string {
  if (rawFragment) return EMAIL_CENTER_TABS.LAYOUT;
  if (rawTab === LEGACY_TABS.THEME && rawSection === LEGACY_DESIGN_SECTION) {
    return EMAIL_CENTER_TABS.LAYOUT;
  }
  if (rawTab !== LEGACY_TABS.SETTINGS) return rawTab;
  return rawSection === LEGACY_SETTINGS_SECTIONS.TRANSLATIONS
    ? LEGACY_TABS.TRANSLATIONS
    : EMAIL_CENTER_TABS.MAILERS;
}

/**
 * Where an old Email Center link has to go now, or null if it stays put.
 *
 * Theme and Translations were tabs of this page and are Touchpoints pages of
 * their own; `?tab=theme&section=brand&journey=x` has to become
 * `/touchpoints/design?section=brand&journey=x` rather than fall back to
 * Templates. Returns a full `pathname?search` so the caller can replace the
 * current entry with it.
 */
export function legacyTabRedirect(search: string): string | null {
  const params = new URLSearchParams(search || '');
  const tab = migrateLegacyTab(
    params.get('tab') || '',
    params.get('section') || '',
    params.get('fragment') || '',
  );

  if (tab !== LEGACY_TABS.THEME && tab !== LEGACY_TABS.TRANSLATIONS) {
    return null;
  }

  const state = parseEmailCenterSearch(search);
  const next =
    tab === LEGACY_TABS.THEME
      ? ROUTE_PATHS.TOUCHPOINTS_DESIGN
      : ROUTE_PATHS.TOUCHPOINTS_TRANSLATIONS;

  // `tab` and `section` are re-derived from the state, so the forwarded link
  // carries the section (Design) and drops it again (Translations) by itself.
  return `${next}${buildEmailCenterSearch({
    ...state,
    tab: DEFAULT_URL_STATE.tab,
    themeSection:
      tab === LEGACY_TABS.THEME ? state.themeSection : DEFAULT_THEME_SECTION,
  })}`;
}

/**
 * Where a Design-page link that addressed the retired Email design section has
 * to go, or null if it stays put.
 *
 * The layouts & building blocks are the Email Center's Layout & Components tab
 * now, so `/touchpoints/design?section=design` — and a block deep-link, which
 * names no section — has to become
 * `/touchpoints/email-center?tab=layout[&fragment=…]` rather than fall back to
 * Colours. The mirror image of `legacyTabRedirect`, for the page the content
 * left.
 */
export function legacyDesignRedirect(search: string): string | null {
  const params = new URLSearchParams(search || '');
  const addressesLayout =
    params.get('section') === LEGACY_DESIGN_SECTION || !!params.get('fragment');
  if (!addressesLayout) return null;

  const state = parseEmailCenterSearch(search);
  // The section is dropped and the tab re-derived, so the forwarded link carries
  // only what the Layout tab reads (the fragment, and the journey it was on).
  return `${ROUTE_PATHS.EMAIL_CENTER}${buildEmailCenterSearch({
    ...state,
    tab: EMAIL_CENTER_TABS.LAYOUT,
    themeSection: DEFAULT_THEME_SECTION,
  })}`;
}

export function parseEmailCenterSearch(search: string): EmailCenterUrlState {
  const params = new URLSearchParams(search || '');

  const rawTab = migrateLegacyTab(
    params.get('tab') || '',
    params.get('section') || '',
    params.get('fragment') || '',
  );
  const tab = (
    TAB_VALUES.includes(rawTab) ? rawTab : DEFAULT_URL_STATE.tab
  ) as EmailCenterTab;

  const journeyId = params.get('journey') || null;
  const fragmentKey = params.get('fragment') || null;

  // Unknown values (a bookmarked `?section=mailers`, a section that has since
  // been renamed or moved out, such as `design`) fall back to the default rather
  // than render nothing.
  const themeSection = parseThemeSection(params.get('section'));

  // view/type only mean something while a template is addressed.
  const templateId = params.get('template') || null;
  const templateView = templateId ? params.get('view') || null : null;
  const templateType =
    templateId === NEW_TEMPLATE_ID ? params.get('type') || null : null;

  return {
    tab,
    themeSection,
    journeyId,
    fragmentKey,
    templateId,
    templateView,
    templateType,
  };
}

export function buildEmailCenterSearch(state: EmailCenterUrlState): string {
  const params = new URLSearchParams();

  if (state.tab !== DEFAULT_URL_STATE.tab) params.set('tab', state.tab);
  // Only when it is not the section that opens by default — links stay minimal.
  // The Design page is the only surface that sets this, so no other page picks
  // up a stray `section`.
  if (state.themeSection !== DEFAULT_THEME_SECTION) {
    params.set('section', state.themeSection);
  }
  if (state.journeyId) params.set('journey', state.journeyId);
  if (state.fragmentKey) params.set('fragment', state.fragmentKey);
  if (state.templateId) {
    params.set('template', state.templateId);
    if (state.templateView) params.set('view', state.templateView);
    if (state.templateId === NEW_TEMPLATE_ID && state.templateType)
      params.set('type', state.templateType);
  }

  const search = params.toString();
  return search ? `?${search}` : '';
}
